import { memo } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import { CoachSports, SportsEnum } from 'types/generated/client';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import classNames from 'styles/utils/classNames';
import ExploreLessonCard from './ExploreLessonCard';
import { CoachType } from './props';
import { isCoachProfile, isInstantBookEligible } from './utils';

const MemoizedExploreLessonCard = memo(ExploreLessonCard);

interface CoachGridProps {
  coaches: CoachType[];
  listHoveredCoachId: string | null;
  setListHoveredCoachId: (id: string | null) => void;
  activeSport: SportsEnum;
  vairProMap: Record<string, boolean>;
}

const CoachGrid: React.FC<CoachGridProps> = ({
  coaches,
  listHoveredCoachId,
  setListHoveredCoachId,
  activeSport,
  vairProMap,
}) => {
  return (
    <div className="mx-auto grid w-full grid-cols-1 gap-ds-xl px-4 xs:grid-cols-1 sm:grid-cols-2 sm:px-8 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3">
      {coaches.map((coach) => {
        const coachEligibleForInstantBook = isCoachProfile(coach)
          ? isInstantBookEligible(coach)
          : false;

        return (
          <div
            key={coach.id}
            className={classNames(
              'w-full flex-shrink-0 overflow-hidden rounded-lg lg:max-w-[26rem]',
              listHoveredCoachId === coach.id &&
                'ring-2 ring-color-bg-lightmode-invert ring-offset-8 ring-offset-color-bg-darkmode-invert dark:ring-color-bg-darkmode-invert dark:ring-offset-color-bg-lightmode-invert',
            )}
            onMouseEnter={() => setListHoveredCoachId(coach.id)}
            onMouseLeave={() => setListHoveredCoachId(null)}
          >
            <MemoizedExploreLessonCard
              key={coach.id}
              id={coach.id}
              slug={isCoachProfile(coach) ? coach.username : coach.id}
              photoUrl={
                isCoachProfile(coach)
                  ? getProfileImageUrlOrPlaceholder({
                      path: coach.profileImagePath || '',
                      placeholder: EMPTY_COACH_AVATAR_SRC,
                    })
                  : coach.profileImageUrl ||
                    coach.aboutPhotoImageUrl ||
                    coach.logoImageUrl ||
                    EMPTY_COACH_AVATAR_SRC
              }
              certificationImages={[]}
              reviews={
                isCoachProfile(coach)
                  ? {
                      averageRating:
                        (coach.coachReviewsAggregate?.aggregate?.avg?.rating as number | null) || 0,
                      totalReviews:
                        (coach.coachReviewsAggregate?.aggregate?.count as number | null) || 0,
                    }
                  : null
              }
              defaultSport={isCoachProfile(coach) ? coach.defaultSport || '' : undefined}
              coachSports={isCoachProfile(coach) ? (coach.coachSports as CoachSports[]) : []}
              fullName={isCoachProfile(coach) ? coach.fullName || '' : coach?.name || ''}
              price={isCoachProfile(coach) ? coach.priceUnitAmountCoachDefault || 0 : 0}
              lessonGiven={
                isCoachProfile(coach) ? coach.coachLessonsAggregate.aggregate?.count || 0 : 0
              }
              distance={coach.distance}
              isProfessionalRacquetRegistryActive={
                isCoachProfile(coach) ? coach.isProfessionalRacquetRegistryActive || false : false
              }
              currency={
                isCoachProfile(coach)
                  ? coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD
                  : CURRENCY_USD
              }
              activeSport={activeSport}
              isInstantBookEligible={coachEligibleForInstantBook}
              isPciMember={isCoachProfile(coach) ? coach.isPciMember || false : false}
              isVairPro={!!vairProMap[coach.id]}
              impressionSource="BROWSE"
            />
          </div>
        );
      })}
    </div>
  );
};

export default CoachGrid;
